import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './OpenQuestionPractice.css';

const OpenQuestionPractice = () => {
  const { oppositionName, lessonName } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [grading, setGrading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);

  const decodedLesson = useMemo(() => decodeURIComponent(lessonName || ''), [lessonName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `/api/open_questions/lesson/${encodeURIComponent(oppositionName)}/${encodeURIComponent(decodedLesson)}`
        );
        if (!cancelled) setQuestions(res.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('No se pudieron cargar las preguntas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [oppositionName, decodedLesson]);

  const current = questions[activeIdx];

  const resetForCurrent = () => {
    setAnswer('');
    setFeedback(null);
    setError(null);
  };

  const goPrev = () => { if (activeIdx > 0) { setActiveIdx(activeIdx - 1); resetForCurrent(); } };
  const goNext = () => { if (activeIdx < questions.length - 1) { setActiveIdx(activeIdx + 1); resetForCurrent(); } };

  const submit = async () => {
    if (!current || !answer.trim()) return;
    setGrading(true);
    setError(null);
    setFeedback(null);
    try {
      const res = await axios.post(`/api/open_questions/${current.id}/grade`, { answer });
      setFeedback(res.data);
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail || 'Error al corregir';
      setError(typeof detail === 'string' ? detail : 'Error al corregir');
    } finally {
      setGrading(false);
    }
  };

  if (loading) return <div className="page-loader">Cargando preguntas…</div>;

  return (
    <div className="open-q-page">
      <button onClick={() => navigate(`/oposicion/${oppositionName}/preguntas-abiertas`)} className="back-link">← Volver</button>
      <header className="page-header">
        <h1 className="page-title">Preguntas abiertas — {decodedLesson}</h1>
        <p className="page-subtitle">{oppositionName} · responde con tus propias palabras</p>
      </header>

      {questions.length === 0 ? (
        <div className="empty-state">
          <p>Todavía no hay preguntas abiertas para este tema.</p>
          <p style={{ fontSize: '0.85em', color: '#777' }}>
            Generar con:{' '}
            <code>python examenes/genera_preguntas_abiertas.py --tema "{decodedLesson}" --oposicion {oppositionName} --n 5</code>
          </p>
        </div>
      ) : (
        <>
          <div className="open-q-progress">
            <span>Pregunta {activeIdx + 1} de {questions.length}</span>
            <div className="open-q-nav">
              <button onClick={goPrev} disabled={activeIdx === 0}>◀ Anterior</button>
              <button onClick={goNext} disabled={activeIdx === questions.length - 1}>Siguiente ▶</button>
            </div>
          </div>

          <div className="open-q-card">
            <div className="open-q-meta">
              {current.law && current.article ? (
                <Link
                  to={`/article/${encodeURIComponent(current.law)}/${encodeURIComponent(current.article)}`}
                  className="open-q-tag open-q-tag-link"
                >
                  {current.law} · {current.article}
                </Link>
              ) : (
                <>
                  {current.law && <span className="open-q-tag">{current.law}</span>}
                  {current.article && <span className="open-q-tag">{current.article}</span>}
                </>
              )}
              {current.difficulty && <span className="open-q-tag tag-diff">Dificultad {current.difficulty}</span>}
            </div>
            <h2 className="open-q-text">{current.question_text}</h2>

            <textarea
              className="open-q-textarea"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Escribe tu respuesta con tus palabras..."
              rows={8}
              disabled={grading}
            />

            <div className="open-q-actions">
              <button className="primary-btn" onClick={submit} disabled={grading || !answer.trim()}>
                {grading ? 'Corrigiendo…' : 'Corregir respuesta'}
              </button>
              {feedback && (
                <button className="secondary-btn" onClick={resetForCurrent}>
                  Limpiar
                </button>
              )}
            </div>

            {error && <div className="open-q-error">{error}</div>}

            {feedback && (
              <div className="open-q-feedback">
                <div className="open-q-score">
                  Puntuación: <strong>{Math.round((feedback.score || 0) * 100)}%</strong>
                </div>
                {feedback.feedback && (
                  <p className="open-q-feedback-text">{feedback.feedback}</p>
                )}
                {Array.isArray(feedback.points_status) && feedback.points_status.length > 0 && (
                  <div className="open-q-points">
                    <h4>Puntos clave</h4>
                    <ul>
                      {feedback.points_status.map((p, i) => (
                        <li key={i} className={p.covered ? 'point-ok' : 'point-miss'}>
                          <span className="point-mark">{p.covered ? '✓' : '✗'}</span>
                          <span className="point-text">
                            <strong>{p.point}</strong>
                            {p.note && <span className="point-note"> — {p.note}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {Array.isArray(feedback.extra_credit) && feedback.extra_credit.length > 0 && (
                  <div className="open-q-extras">
                    <h4>Extras válidos que mencionaste</h4>
                    <ul>
                      {feedback.extra_credit.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OpenQuestionPractice;
